import { Page } from "@/components/Reveal";
import SiteEnd from "@/components/SiteEnd";

const sections = [
  {
    heading: "1. Information We Collect",
    body: "When you contact us through our website, we may collect your name, email address, phone number, and any details you share in the contact form. We also collect standard analytics data such as page views, browser type, and referring URLs to improve our website experience. We do not collect payment information directly — any billing is handled through separate secure channels.",
  },
  {
    heading: "2. How We Use Your Information",
    body: "We use the information you provide to respond to your enquiries, send proposals and project updates, and improve our services. We do not sell, rent, or share your personal information with third parties for marketing purposes. Information may be shared with trusted service providers (e.g. email platforms, analytics tools) who are bound by confidentiality obligations.",
  },
  {
    heading: "3. Cookies and Analytics",
    body: "Our website may use cookies and third-party analytics tools (such as Google Analytics) to understand how visitors interact with our site. Cookies are small files stored on your device that help us improve your browsing experience. You may disable cookies through your browser settings, though this may affect certain website functionality.",
  },
  {
    heading: "4. Data Retention",
    body: "We retain personal information only as long as necessary to fulfil the purpose for which it was collected, or as required by applicable law. Contact enquiries are retained for up to 24 months. You may request deletion of your data at any time by contacting us.",
  },
  {
    heading: "5. Data Security",
    body: "We implement reasonable technical and organisational measures to protect your personal information from unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure and we cannot guarantee absolute security.",
  },
  {
    heading: "6. Third-Party Links",
    body: "Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party sites you visit.",
  },
  {
    heading: "7. Your Rights",
    body: "You have the right to access, correct, or request deletion of personal information we hold about you. To exercise these rights, please contact us at info@ophodigital.com. We will respond to your request within a reasonable timeframe.",
  },
  {
    heading: "8. Disclaimer",
    body: "The content on the OPHO Digital website is provided for general informational purposes only. While we strive for accuracy, we make no representations or warranties of any kind regarding the completeness, accuracy, reliability, or suitability of any information on the site. Any reliance you place on such information is strictly at your own risk. OPHO Digital does not guarantee specific marketing results, rankings, or business outcomes from the use of our services.",
  },
  {
    heading: "9. Changes to This Policy",
    body: "We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date. Continued use of our website after changes are posted constitutes your acceptance of the revised policy.",
  },
];

export default function Privacy() {
  return (
    <Page testId="privacy-page">
      {/* Hero with blob+waves background, no globe/dash */}
      <div className="relative pt-28 sm:pt-32 pb-10 overflow-x-clip">
        <img src="/assets/blob.png" className="absolute -top-40 left-1/2 -translate-x-1/2 w-[110%] max-w-none opacity-70 pointer-events-none select-none" alt="" />
        <img src="/assets/waves.png" className="absolute top-[30px] sm:top-[60px] left-1/2 -translate-x-1/2 w-[118%] max-w-none pointer-events-none select-none" alt="" />
        <div className="relative site-container">
          <div className="flex items-end justify-between gap-8 sm:gap-16">
            <h1 data-testid="privacy-page-heading" className="text-[40px] sm:text-[52px] lg:text-[64px] font-medium leading-[1.1] tracking-[-0.03em] text-[#111]">
              Privacy Policy &amp; Disclaimer.
            </h1>
            <span className="text-base sm:text-xl font-medium text-black/70 shrink-0 self-end pb-1">August 2026</span>
          </div>
        </div>
      </div>
      <section className="relative z-10 w-full px-6 mt-1">
        <div className="glass-surface relative rounded-[32px] px-6 sm:px-16 lg:px-[92px] py-16 lg:py-24 overflow-hidden">
          <div className="max-w-[758px]">
            <p className="text-base leading-relaxed text-[#111]/60">
              OPHO Digital is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your information when you visit our website or engage our services.
            </p>

            <div className="mt-10 space-y-10">
              {sections.map((s) => (
                <div key={s.heading}>
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#111] tracking-tight">{s.heading}</h2>
                  <p className="mt-2 text-base leading-relaxed text-[#111]/60">{s.body}</p>
                </div>
              ))}

              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-[#111] tracking-tight">10. Contact</h2>
                <p className="mt-2 text-base leading-relaxed text-[#111]/60">
                  For privacy-related questions or data requests, please contact us at{" "}
                  <a href="mailto:info@ophodigital.com" className="text-[#8C5EBA] hover:underline">
                    info@ophodigital.com
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <SiteEnd />
    </Page>
  );
}
